import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { AppState, type AppStateStatus } from 'react-native';

import { useAuth } from './AuthContext';
import { purgeLegacyFinanceCaches } from '../core/storage/purgeLegacyFinanceCaches';
import { deriveHousehold } from '../data/deriveHousehold';
import { sampleHouseholdCore } from '../data/sampleHouseholdData';
import type {
  CoreHousehold,
  DeriveHouseholdOptions,
  DerivedHouseholdView,
} from '../types/financial';

function emptyLiveCore(): CoreHousehold {
  const now = new Date();
  const asOfMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  return {
    profile: {
      countryCode: 'US',
      currency: 'USD',
      householdName: '',
      monthlyIncome: 0,
      savingsRateTarget: 0.22,
      asOfMonth,
    },
    accounts: [],
    transactions: [],
    balanceObservations: [],
  };
}

function cloneCore(core: CoreHousehold): CoreHousehold {
  return JSON.parse(JSON.stringify(core)) as CoreHousehold;
}

type SetCoreInput = CoreHousehold | ((prev: CoreHousehold) => CoreHousehold);

type HouseholdContextValue = {
  isHydrated: boolean;
  /** Bumps when core changes so selectors re-run. */
  revision: number;
  core: CoreHousehold;
  getCore: () => CoreHousehold;
  setCore: (next: SetCoreInput) => Promise<void>;
  patchCore: (patch: Partial<CoreHousehold>) => Promise<void>;
  /** Always re-derives from current in-memory core — never reads persisted rollups. */
  selectDerived: (options?: DeriveHouseholdOptions) => DerivedHouseholdView;
  /** Re-read fixture (guests) or empty live core. */
  refresh: () => Promise<void>;
  /** Replace store with bundled fixture and clear persisted core. */
  resetToFixture: () => Promise<void>;
};

const HouseholdContext = createContext<HouseholdContextValue | null>(null);

export function HouseholdProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [revision, setRevision] = useState(0);
  const [core, setCoreState] = useState<CoreHousehold>(() => emptyLiveCore());
  const coreRef = useRef(core);
  coreRef.current = core;

  const applyCore = useCallback((next: CoreHousehold, _persist: boolean) => {
    const cloned = cloneCore(next);
    coreRef.current = cloned;
    setCoreState(cloned);
    setRevision((r) => r + 1);
  }, []);

  const hydrateFromStorage = useCallback(async () => {
    await purgeLegacyFinanceCaches();
    if (isAuthenticated) {
      applyCore(emptyLiveCore(), false);
      return;
    }
    applyCore(sampleHouseholdCore, false);
  }, [applyCore, isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    let mounted = true;
    hydrateFromStorage().finally(() => {
      if (mounted) setIsHydrated(true);
    });
    return () => {
      mounted = false;
    };
  }, [authLoading, hydrateFromStorage]);

  const refresh = useCallback(async () => {
    if (isAuthenticated) return;
    await hydrateFromStorage();
  }, [hydrateFromStorage, isAuthenticated]);

  useEffect(() => {
    const onChange = (status: AppStateStatus) => {
      if (status === 'active') {
        void refresh();
      }
    };
    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [refresh]);

  const getCore = useCallback(() => coreRef.current, []);

  const setCore = useCallback(
    async (next: SetCoreInput) => {
      const resolved = typeof next === 'function' ? next(coreRef.current) : next;
      applyCore(resolved, true);
    },
    [applyCore],
  );

  const patchCore = useCallback(
    async (patch: Partial<CoreHousehold>) => {
      applyCore({ ...coreRef.current, ...patch }, true);
    },
    [applyCore],
  );

  const selectDerived = useCallback(
    (options?: DeriveHouseholdOptions) => deriveHousehold(coreRef.current, options),
    // revision ensures callers that close over selectDerived see fresh derives after edits
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional revision gate
    [revision],
  );

  const resetToFixture = useCallback(async () => {
    applyCore(sampleHouseholdCore, false);
  }, [applyCore]);

  const value = useMemo<HouseholdContextValue>(
    () => ({
      isHydrated,
      revision,
      core,
      getCore,
      setCore,
      patchCore,
      selectDerived,
      refresh,
      resetToFixture,
    }),
    [
      isHydrated,
      revision,
      core,
      getCore,
      setCore,
      patchCore,
      selectDerived,
      refresh,
      resetToFixture,
    ],
  );

  return <HouseholdContext.Provider value={value}>{children}</HouseholdContext.Provider>;
}

export function useHousehold(): HouseholdContextValue {
  const ctx = useContext(HouseholdContext);
  if (!ctx) {
    throw new Error('useHousehold must be used within HouseholdProvider');
  }
  return ctx;
}

/**
 * Re-derive the UI view model from store core on every revision / period change.
 * Never reads persisted rollups.
 */
export function useHouseholdDerived(
  options?: DeriveHouseholdOptions,
): DerivedHouseholdView {
  const { core, revision } = useHousehold();
  const period = options?.period ?? 'month';
  const periodKey = options?.periodKey;

  return useMemo(
    () => deriveHousehold(core, { period, periodKey }),
    [core, revision, period, periodKey],
  );
}
